/**
 * §A · Los tipos del back office de plataforma.
 *
 * ## Esto no es el panel con otro color
 *
 * El panel es una herramienta para llevar **un** club; esto es una sala de
 * máquinas para llevar doscientos. Las preguntas son otras —a quién llamo hoy,
 * quién no nos ha pagado, qué proceso falló anoche— y por eso no comparten ni una
 * pantalla ni un tipo.
 *
 * ## Lo que aquí se ve y en el panel no
 *
 * Todo. `kiap_platform` es `BYPASSRLS`: desde aquí se ven los datos de cualquier
 * club sin que ninguna política los filtre. Eso es lo que obliga a que **entrar a
 * mirar deje rastro** —modo soporte con motivo, caducidad y aviso al club— y a que
 * el registro de auditoría sea una pantalla y no una tabla que nadie abre.
 */

import { numero } from '@/shared/ui/formato';

export type EstadoDeClub = 'trial' | 'active' | 'suspended' | 'churned';
export type NivelDeRiesgo = 'ok' | 'watch' | 'at_risk' | 'critical';

export const etiquetaDeEstadoDeClub = (estado: string): string =>
  ({
    trial: 'De prueba',
    active: 'Activo',
    suspended: 'Suspendido',
    churned: 'Se fue',
  })[estado] ?? estado;

/**
 * El riesgo, en palabras y con su color.
 *
 * Cuatro niveles y no un número, porque «73» no es una decisión. El orden importa
 * para pintar: lo crítico en rojo y lo vigilado en ámbar, y **`ok` sin color** —si
 * todo lleva color, el color deja de avisar—.
 */
export const RIESGOS: Record<string, { etiqueta: string; clase: string }> = {
  ok: { etiqueta: 'Bien', clase: 'pastilla-bien' },
  watch: { etiqueta: 'Vigilar', clase: 'pastilla-paused' },
  at_risk: { etiqueta: 'En riesgo', clase: 'pastilla-paused' },
  critical: { etiqueta: 'Crítico', clase: 'pastilla-mal' },
};

export interface ClubResumen {
  readonly id: string;
  readonly slug: string;
  readonly trade_name: string;
  readonly status: EstadoDeClub;
  readonly plan_code: string | null;
  readonly plan_name: string | null;
  readonly price_cents: number | null;
  readonly billing_period: 'monthly' | 'quarterly' | 'annual' | null;
  readonly subscription_status: string | null;
  readonly student_count: number;
  readonly health_score: number | null;
  readonly risk_level: NivelDeRiesgo | null;
  readonly measured_on: Date | string | null;
  readonly onboarded_at: Date | string | null;
  readonly created_at: Date | string;
  /** Club de demostración: no es un cliente, y la lista no lo suma. */
  readonly is_demo: boolean;
}

export interface ClubDetalle extends ClubResumen {
  readonly legal_name: string | null;
  readonly tax_id: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly website: string | null;
  readonly country: string;
  readonly timezone: string;
  readonly currency: string;
  readonly churned_at: Date | string | null;
  readonly trial_ends_on: Date | string | null;
  readonly owner: {
    readonly user_id: string;
    readonly name: string;
    readonly email: string;
    /** Invitado y sin entrar todavía. */
    readonly pending: boolean;
  } | null;
  readonly venue_count: number;
  readonly staff_count: number;
  readonly disciplines: readonly string[];
  readonly dpa_version: string | null;
  readonly dpa_signed_at: Date | string | null;
}

/** El uso real, que es lo que distingue un club vivo de uno que pagó y no entró. */
export interface UsoDelClub {
  readonly window_days: number;
  readonly sessions_held: number;
  readonly roll_calls: number;
  readonly attendance_marks: number;
  readonly active_students: number;
  readonly guardians: number;
  readonly guardians_with_app: number;
  readonly announcements: number;
  readonly feed_items: number;
  readonly billing_runs: number;
  readonly last_activity_at: Date | string | null;
}

export interface Suscripcion {
  readonly id: string;
  readonly plan_code: string;
  readonly plan_name: string | null;
  readonly status: string;
  readonly price_cents: number;
  readonly billing_period: string;
  readonly started_on: Date | string;
  readonly ends_on: Date | string | null;
  readonly trial_ends_on: Date | string | null;
  /** Días tras vencer el pago en que todo sigue funcionando (§A.2.1). */
  readonly grace_days: number;
}

export interface VistaDeSuscripcion {
  readonly active: Suscripcion | null;
  readonly history: readonly Suscripcion[];
}

/**
 * Cómo nos paga un club. **Sin domiciliación ni tarjeta** (H-14): nos hacen una
 * transferencia o un Bizum y lo anotamos aquí.
 */
export type MetodoDePago = 'transfer' | 'bizum' | 'cash' | 'card' | 'other';

export const METODOS_DE_PAGO: readonly {
  valor: MetodoDePago;
  etiqueta: string;
}[] = [
  { valor: 'transfer', etiqueta: 'Transferencia' },
  { valor: 'bizum', etiqueta: 'Bizum' },
  { valor: 'cash', etiqueta: 'Efectivo' },
  { valor: 'other', etiqueta: 'Otro' },
];

export const etiquetaDeMetodoDePago = (metodo: string): string =>
  ({
    transfer: 'Transferencia',
    bizum: 'Bizum',
    cash: 'Efectivo',
    card: 'Tarjeta',
    other: 'Otro',
  })[metodo] ?? metodo;

export interface PagoDelClub {
  readonly id: string;
  readonly academy_id: string;
  readonly subscription_id: string | null;
  /** El mes que cubre, anclado al día 1. `null` si es un ajuste. */
  readonly period: Date | string | null;
  readonly amount_cents: number;
  readonly method: MetodoDePago;
  readonly reference: string | null;
  /** Cuándo entró el dinero, que **no** es cuándo se tecleó. */
  readonly paid_on: Date | string;
  /** Hasta cuándo da servicio. El club está pagado hasta la mayor (§A.2.1). */
  readonly covers_until: Date | string;
  readonly recorded_by_email: string | null;
  readonly note: string | null;
  readonly created_at: Date | string;
}

export interface ResumenDePagos {
  /** Lo cobrado de verdad. **No tiene por qué cuadrar con el MRR**, que es lo contratado. */
  readonly collected_cents: number;
  readonly payments: number;
  readonly last_paid_on: Date | string | null;
  readonly paid_through: Date | string | null;
  /** `due`: en la gracia. `lapsed`: panel del club en solo lectura. */
  readonly standing: SituacionDelPago;
  readonly grace_ends_on: Date | string | null;
}

export type SituacionDelPago = 'current' | 'due' | 'lapsed' | 'none';

export const SITUACIONES_DEL_PAGO: Record<
  SituacionDelPago,
  { etiqueta: string; clase: string }
> = {
  current: { etiqueta: 'Al día', clase: 'pastilla-bien' },
  due: { etiqueta: 'En la gracia', clase: 'pastilla-espera' },
  lapsed: { etiqueta: 'Solo lectura', clase: 'pastilla-mal' },
  none: { etiqueta: 'Sin suscripción', clase: '' },
};

/** Un club que se pasa de su plan o que nos debe · `GET /admin/plan-alerts`. */
export interface AvisoDePlan {
  readonly academy_id: string;
  readonly trade_name: string;
  readonly plan_code: string;
  readonly plan_name: string;
  readonly usage: {
    readonly students: { readonly used: number; readonly max: number | null };
    readonly venues: { readonly used: number; readonly max: number | null };
  };
  readonly over_limit: boolean;
  readonly standing: SituacionDelPago;
  readonly paid_through: Date | string | null;
  readonly grace_ends_on: Date | string | null;
  readonly limits_alerted_at: Date | string | null;
}

export interface PaginaDePagos {
  readonly data: readonly PagoDelClub[];
  readonly summary: ResumenDePagos;
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

export interface Persona {
  readonly user_id: string;
  readonly email: string;
  readonly name: string;
  /** La primera respuesta a «no me llega el correo» y a «no puedo entrar». */
  readonly email_verified: boolean;
  readonly is_platform_staff: boolean;
  readonly memberships: number;
  readonly last_seen_at: Date | string | null;
  readonly created_at: Date | string;
}

export interface PersonaDetalle extends Persona {
  readonly locale: string | null;
  readonly clubs: readonly {
    readonly academy_id: string;
    readonly academy_name: string;
    readonly role: string;
    readonly status: string;
    readonly accepted_at: Date | string | null;
  }[];
  readonly students: readonly {
    readonly student_id: string;
    readonly first_name: string;
    readonly academy_name: string;
    readonly access_level: string;
  }[];
}

export interface SesionDeSoporte {
  readonly id: string;
  readonly academy_id: string;
  readonly academy_name: string;
  readonly staff_name: string;
  readonly reason: string;
  readonly started_at: Date | string;
  readonly ended_at: Date | string | null;
  readonly expires_at: Date | string;
  /** **No es «no tiene `ended_at`»**: es eso y que no haya caducado. Lo resuelve el servidor. */
  readonly active: boolean;
  readonly staff_user_id: string;
  /** La primera vez que pudo cambiar datos del club, o `null` si solo miró. No se borra. */
  readonly write_enabled_at: Date | string | null;
  /** Si **ahora** puede escribir: «Entrar en modo escritura» encendido. */
  readonly writable: boolean;
  /** `academies.status`, para que el panel en soporte diga lo que vería el club. */
  readonly academy_status: string;
}

export interface Proceso {
  readonly name: string;
  readonly cadence: string;
  /** Cuándo, en palabras. */
  readonly schedule: string;
  readonly phase: number;
  /** Si hoy se puede lanzar a mano. Casi ninguno todavía. */
  readonly runnable: boolean;
  readonly last_run_at: Date | string | null;
  readonly last_status: string | null;
  readonly last_duration_ms: number | null;
  readonly last_error: string | null;
  readonly failed_last_24h: number;
  readonly runs_last_24h: number;
  readonly never_run: boolean;
  /**
   * **La alerta que más se olvida.** Un proceso que no se ejecuta no genera ningún
   * error, así que hay que preguntárselo: esto compara la última vuelta con la
   * cadencia declarada, no con las filas que hay.
   */
  readonly overdue: boolean;
  /** Tardó más del triple que su **mediana**, no que su media. */
  readonly slow: boolean;
}

export interface EjecucionDeProceso {
  readonly id: string;
  readonly job: string;
  readonly academy_name: string | null;
  readonly scope_key: string;
  readonly status: 'running' | 'completed' | 'failed' | 'skipped';
  readonly started_at: Date | string;
  readonly finished_at: Date | string | null;
  readonly duration_ms: number | null;
  readonly items_processed: number;
  readonly items_failed: number;
  readonly error: string | null;
}

/** Cuánto tardó, legible. Los milisegundos solo se leen por debajo del segundo. */
export function duracion(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${numero(ms / 1000)} s`;
  return `${Math.round(ms / 60_000)} min`;
}

/**
 * El color de un proceso según cómo acabó.
 *
 * Un proceso **que no ha corrido nunca** no es un fallo y no se pinta en rojo: es
 * uno que todavía no tocaba. Pintarlo igual que uno caído es cómo una pantalla de
 * vigilancia acaba siendo un muro rojo que nadie mira.
 */
export function claseDeProceso(estado: string | null): string {
  if (estado === 'completed') return 'pastilla-bien';
  if (estado === 'failed') return 'pastilla-mal';
  if (estado === 'running') return 'pastilla-info';
  if (estado === 'skipped') return 'pastilla-paused';
  return 'pastilla-left';
}

export const etiquetaDeProceso = (estado: string | null): string =>
  ({
    completed: 'Bien',
    failed: 'Falló',
    running: 'Corriendo',
    skipped: 'Saltado',
  })[estado ?? ''] ?? 'Nunca';

/* ── §A.1 · La cronología de la cuenta ────────────────────────────────────── */

/**
 * Una entrada de `GET /admin/academies/{id}/timeline`.
 *
 * **Lo que se mira antes de llamar**, y el contrato es explícito sobre por qué en una
 * sola columna: «repartido en seis pantallas nadie lo cruza; junto se ve que la
 * remesa lleva tres meses fallando y que por eso llaman».
 */
export type ClaseDeEntrada =
  | 'alta'
  | 'onboarding'
  | 'suscripcion'
  | 'proceso'
  | 'incidencia'
  | 'soporte'
  | 'baja';

export interface EntradaDeCronologia {
  readonly at: Date | string;
  readonly kind: ClaseDeEntrada;
  readonly title: string;
  readonly detail: string | null;
}

/**
 * Cómo se pinta cada clase de entrada.
 *
 * **Solo dos llevan color**, y es a propósito: la incidencia en rojo y la baja en
 * ámbar. Si el alta, la suscripción y cada remesa llevaran el suyo, la columna sería
 * un semáforo roto y habría que leerla entera para encontrar lo que importa. Es la
 * misma regla que `RIESGOS`, donde `ok` va sin color.
 *
 * El icono no sustituye a la palabra: va con `aria-hidden` y la clase se dice al lado
 * en texto, porque un lector de pantalla no lee un emoji como una categoría.
 */
export const CLASES_DE_ENTRADA: Record<
  ClaseDeEntrada,
  { etiqueta: string; icono: string; clase: string }
> = {
  alta: { etiqueta: 'Alta', icono: '🎉', clase: '' },
  onboarding: { etiqueta: 'Arranque', icono: '🚀', clase: '' },
  suscripcion: { etiqueta: 'Suscripción', icono: '📄', clase: '' },
  proceso: { etiqueta: 'Proceso', icono: '⚙️', clase: '' },
  incidencia: { etiqueta: 'Incidencia', icono: '⚠️', clase: 'entrada-mala' },
  soporte: { etiqueta: 'Soporte', icono: '🔎', clase: '' },
  baja: { etiqueta: 'Baja', icono: '👋', clase: 'entrada-ojo' },
};

export const claseDeEntrada = (
  kind: string
): { etiqueta: string; icono: string; clase: string } =>
  CLASES_DE_ENTRADA[kind as ClaseDeEntrada] ?? {
    etiqueta: kind,
    icono: '•',
    clase: '',
  };

/* ── §A.1 · El resultado del arranque ─────────────────────────────────────── */

/**
 * Lo que devuelve `POST /admin/academies/{id}/onboarding`.
 *
 * `already_onboarded` no es un error: relanzarlo es seguro y **a veces es lo que se
 * quiere**, porque los avisos vuelven a salir solo para quien sigue sin entrar. La
 * pantalla lo dice con esas palabras en vez de esconder el botón.
 */
export interface ResultadoDeArranque {
  readonly onboarded_at: Date | string;
  readonly guardians_invited: number;
  readonly guardians_already_in: number;
  readonly poster_url: string;
  readonly already_onboarded: boolean;
}

/* ── §A.5 · Interruptores ─────────────────────────────────────────────────── */

export interface Interruptor {
  readonly id: string;
  /** `null` es la fila **global**: la decisión por defecto para todos. */
  readonly academy_id: string | null;
  readonly academy_name: string | null;
  readonly flag: string;
  readonly enabled: boolean;
  readonly note: string | null;
  readonly updated_at: Date | string;
}

export interface InterruptorGlobal {
  readonly flag: string;
  readonly enabled_globally: boolean;
  readonly note: string | null;
  /** Los clubes con decisión propia. Es a cuántos está sacada una función. */
  readonly overrides: readonly {
    readonly academy_id: string;
    readonly academy_name: string;
    readonly enabled: boolean;
  }[];
}

/**
 * El nombre de un interruptor, en algo legible.
 *
 * Los interruptores se llaman `segunda_mano.activo` o `muro.fotos` —el patrón del DTO
 * es `^[a-z0-9_.]+$`— y **no hay tabla de nombres bonitos, ni la va a haber**: se
 * crean escribiendo uno nuevo en este formulario, así que cualquier lista que
 * mantuviéramos aquí llegaría siempre tarde.
 *
 * Así que se arregla la forma y no el contenido: puntos y guiones bajos a espacios, y
 * la primera en mayúscula con `::first-letter`… no, con `slice`, porque esto es una
 * cadena y no un nodo. Lo que **no** se hace es `capitalize`: ya nos convirtió
 * «septiembre de 2026» en «Septiembre De 2026» una vez.
 */
export function nombreDeInterruptor(flag: string): string {
  const limpio = flag.replace(/[._]/g, ' ').trim();
  return limpio === '' ? flag : limpio[0]!.toUpperCase() + limpio.slice(1);
}

/* ── §A.2 · Los planes de Kiap ────────────────────────────────────────────── */

/**
 * Un plan del catálogo.
 *
 * `max_students: null` es **ilimitado, no cero**: lo dice el DTO con esas palabras, y
 * leerlo como cero convertiría el plan Academia en el más pequeño de los tres.
 *
 * `subscriptions` es lo que dice si quitar un plan del catálogo deja a alguien
 * colgado, y por eso se pinta en la lista y no en un detalle.
 */
export interface Plan {
  readonly id: string;
  /** No se edita: es lo que enlaza el plan con lo ya contratado. */
  readonly code: string;
  readonly name: string;
  readonly price_cents: number;
  readonly currency: string;
  readonly max_students: number | null;
  readonly max_venues: number | null;
  readonly features: Record<string, unknown>;
  readonly is_public: boolean;
  readonly sort_order: number;
  readonly subscriptions: number;
  readonly created_at: Date | string;
  readonly updated_at: Date | string;
}

/* ── §A.3 · Salud de los clubes ───────────────────────────────────────────── */

/**
 * La salud de un club, **medida por el servidor**.
 *
 * Aquí no se calcula nada y es la regla de esta capa: el score, el nivel de riesgo y
 * el `overdue` de un proceso los resuelve el servidor. Deducir cualquiera de los tres
 * en el cliente es cómo una pantalla de vigilancia acaba mintiendo.
 *
 * ## `attendance_rate: null` no es cero
 *
 * «Un club sin clases en agosto no está en riesgo, está en agosto.» El DTO lo dice
 * así, y el score se calcula entonces sobre lo que sí se puede medir. Un 0 % pintado
 * ahí llamaría a un club que está de vacaciones.
 *
 * ## El MRR va en la fila, y no es adorno
 *
 * «Ordenar por riesgo a secas es engañoso: un club en `critical` de ocho alumnos y
 * uno en `watch` de trescientos no piden la misma llamada esta tarde.»
 */
export interface SaludDeClub {
  readonly academy_id: string;
  readonly slug: string;
  readonly trade_name: string;
  readonly academy_status: string;
  readonly measured_on: Date | string;
  readonly score: number;
  readonly risk_level: string;
  /** `null` es «no se puede medir», no cero. Ver la cabecera. */
  readonly attendance_rate: number | null;
  readonly app_adoption_rate: number | null;
  readonly posts_last_30d: number;
  readonly overdue_rate: number | null;
  readonly students: number;
  readonly mrr_cents: number;
}

export interface SaludConDetalle extends SaludDeClub {
  /** Cada componente con su peso, para poder discutir el número. */
  readonly components: Record<string, unknown>;
  /** «Un 62 no dice nada; un 62 que era 81 hace tres semanas es una llamada.» */
  readonly history: readonly {
    readonly measured_on: Date | string;
    readonly score: number;
    readonly risk_level: string;
  }[];
}

/**
 * Los componentes del score, en español.
 *
 * **Copiados de la migración**, no recordados:
 * `db/migrations/20260910000400_health_scores.sql` construye el `jsonb` con estas
 * cinco claves —`weights`, `attendance`, `app_adoption`, `posts`, `overdue`— y son
 * las que llegan en `components`.
 *
 * `weights` no es un componente: es la tabla de pesos que usó el cálculo. Tiene
 * nombre aquí porque salía como «Weights» en una pantalla en castellano.
 *
 * Lo que **no** se traduce es la nota de cada uno: la escribe la propia función SQL y
 * viene sin acentos —«30 dias»—. Arreglarlo es tocar una migración ya aplicada, así
 * que se deja anotado y no se hace de tapadillo.
 */
export const etiquetaDeComponente = (clave: string): string =>
  ({
    attendance: 'Listas pasadas',
    app_adoption: 'Familias con la app',
    posts: 'Avisos publicados',
    overdue: 'Recibos sin cobrar',
    weights: 'Los pesos del cálculo',
  })[clave] ?? clave;

/* ── §A.3 · ¿Le llegó el aviso? ───────────────────────────────────────────── */

/**
 * La traza de un aviso: **sin el cuerpo, y no por olvido**.
 *
 * «Para un aviso de recibo el cuerpo sería inocuo; para un mensaje de chat es el
 * contenido de una conversación entre una madre y un entrenador, que no leemos.» La
 * consulta del servidor no lo selecciona, así que no hay forma de que se escape.
 */
export interface TrazaDeAviso {
  readonly id: string;
  readonly academy_id: string | null;
  readonly user_id: string;
  readonly user_email: string | null;
  readonly type: string;
  readonly title: string;
  readonly channel: string;
  readonly created_at: Date | string;
  readonly sent_at: Date | string | null;
  readonly read_at: Date | string | null;
  readonly failed_reason: string | null;
}

/**
 * En qué estado está una traza, resuelto en un sitio.
 *
 * Tres estados y el orden de comprobación importa: **el fallo gana**. Un aviso con
 * `failed_reason` y `sent_at` puesto es un reintento que acabó mal, y lo que hay que
 * contarle a quien llama es que no le llegó.
 */
export function estadoDeTraza(t: TrazaDeAviso): {
  etiqueta: string;
  clase: string;
} {
  if (t.failed_reason) return { etiqueta: 'Falló', clase: 'pastilla-mal' };
  if (!t.sent_at) return { etiqueta: 'Sin salir', clase: 'pastilla-paused' };
  if (t.read_at) return { etiqueta: 'Leído', clase: 'pastilla-bien' };
  return { etiqueta: 'Enviado', clase: 'pastilla-info' };
}

/* ── §A.3 · Nuestro propio registro de accesos ────────────────────────────── */

export interface EntradaDeAuditoria {
  readonly id: string;
  readonly academy_id: string | null;
  readonly academy_name: string | null;
  readonly actor_user_id: string | null;
  readonly actor_email: string | null;
  readonly actor_role: string | null;
  readonly action: string;
  readonly entity_type: string | null;
  readonly entity_id: string | null;
  readonly ip: string | null;
  readonly metadata: Record<string, unknown>;
  readonly occurred_at: Date | string;
}

/* ── §A.5 · Webhooks ──────────────────────────────────────────────────────── */

/**
 * Un webhook recibido de un proveedor.
 *
 * «Un webhook de cobro sin procesar es dinero que el club cree que ha entrado», así
 * que los pendientes van primero y no ordenados por fecha a secas. Eso lo hace el
 * servidor; aquí solo se pinta.
 *
 * El cuerpo del evento **no viaja en la lista**: son cargas de decenas de kilobytes y
 * aquí se busca cuál falló, no qué decía.
 *
 * `provider` sigue siendo `gocardless` o `stripe` aunque H-14 quitara la pasarela: la
 * tabla existe y estas rutas también, y el día que haya pasarela esto es lo que la
 * vigila. Mientras no haya, la lista sale vacía, y eso es información.
 */
export interface Webhook {
  readonly id: string;
  readonly provider: 'gocardless' | 'stripe';
  readonly provider_event_id: string;
  readonly event_type: string;
  readonly received_at: Date | string;
  readonly processed_at: Date | string | null;
  readonly error: string | null;
}

/* ── §A.5 · Solicitudes de datos (RGPD) ───────────────────────────────────── */

export type ClaseDeSolicitud = 'export' | 'delete' | 'rectify' | 'restrict';
export type EstadoDeSolicitud =
  'received' | 'in_progress' | 'completed' | 'rejected';

export interface SolicitudDeDatos {
  readonly id: string;
  readonly academy_id: string | null;
  readonly academy_name: string | null;
  readonly student_id: string | null;
  readonly user_id: string | null;
  readonly user_email: string | null;
  readonly kind: ClaseDeSolicitud;
  readonly status: EstadoDeSolicitud;
  readonly requested_at: Date | string;
  readonly due_at: Date | string | null;
  readonly completed_at: Date | string | null;
  /**
   * Días para el plazo legal, **negativo si venció**.
   *
   * «El RGPD da un mes, y una solicitud vencida no es un retraso administrativo: es
   * un incumplimiento con nombre y fecha.» Por eso el número se pinta y no se
   * esconde detrás de un color.
   */
  readonly days_to_due: number | null;
  readonly notes: string | null;
}

/**
 * Las cuatro clases de solicitud, **copiadas del DTO**.
 *
 * `export`, `delete`, `rectify` y `restrict` están en `DataRequestDTO`, no en mi
 * memoria: es la regla de la casa después de inventarse cinco tablas de enum.
 */
export const etiquetaDeSolicitud = (kind: string): string =>
  ({
    export: 'Quiere sus datos',
    delete: 'Quiere que se borren',
    rectify: 'Quiere corregirlos',
    restrict: 'Quiere limitar su uso',
  })[kind] ?? kind;

export const etiquetaDeEstadoDeSolicitud = (estado: string): string =>
  ({
    received: 'Recibida',
    in_progress: 'En curso',
    completed: 'Resuelta',
    rejected: 'Rechazada',
  })[estado] ?? estado;

/**
 * Las funciones que un plan puede incluir · `endpoints.md` §A.2.1
 *
 * Son las etiquetas de `platform_plans.features` **que cierran algo**. Qué plan
 * incluye cada una es dato y se decide en «Planes»; lo que está escrito aquí es solo
 * qué existe y cómo se llama. La base —panel, app del maestro y de familias— la
 * tienen todos y no se ofrece desmarcarla.
 */
export const FUNCIONES_DE_PLAN: readonly {
  etiqueta: string;
  nombre: string;
  explicacion: string;
  reservada?: true;
}[] = [
  {
    etiqueta: 'campeonatos',
    nombre: 'Campeonatos',
    explicacion: 'La sección de competición: convocar, categorías, resultados.',
  },
  {
    etiqueta: 'roles',
    nombre: 'Administrativos y ayudantes',
    explicacion: 'Sin ella, el club tiene dueño y entrenadores y nada más.',
  },
  {
    etiqueta: 'multisede',
    nombre: 'Entrenadores por sede',
    explicacion: 'Limitar a un entrenador a las sedes donde da clase.',
  },
  {
    etiqueta: 'benchmarking',
    nombre: 'Comparación con otros clubes',
    explicacion: 'El informe con la media anonimizada de clubes parecidos.',
  },
  {
    etiqueta: 'api',
    nombre: 'API',
    explicacion: 'Reservada: todavía no hay nada construido que abra.',
    reservada: true,
  },
  {
    etiqueta: 'dominio_propio',
    nombre: 'Dominio propio',
    explicacion: 'Reservada: todavía no hay nada construido que abra.',
    reservada: true,
  },
];

/** La base: la tienen todos los planes y se guarda siempre. */
export const FUNCIONES_BASE = ['panel', 'app_maestro', 'app_familias'] as const;

/** Las etiquetas encendidas de un plan: solo cuenta `true`, igual que el servidor. */
export const funcionesDe = (features: Record<string, unknown>): string[] =>
  Object.entries(features)
    .filter(([, v]) => v === true)
    .map(([k]) => k);
